import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FolderKanban, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { useFreemium } from "@/hooks/useFreemium";
import { usePermissions } from "@/hooks/usePermissions";
import { sortGeneralFirstByName } from "@/lib/utils";
import { ProjectForm, ItemForm, type BudgetItem, type ProjectBudget } from "@/components/finance/ProjectBudgets";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

type OrganizationOption = { id: string; name: string };

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);

const statusColors: Record<string, string> = {
  active: "bg-success/10 text-success border-success/30",
  completed: "bg-muted text-muted-foreground border-border",
  on_hold: "bg-warning/10 text-warning border-warning/30",
};

export default function ProjectBudgetDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useChurch();
  const { user } = useAuth();
  const { isPreview, requireSubscription } = useFreemium();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess, isChurchAdmin } = usePermissions();
  const financeScoped = !isChurchAdmin && hasFinanceScopedAccess();
  const allowedOrgIds = getFinanceOrganizationIds();

  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editItem, setEditItem] = useState<BudgetItem | null>(null);

  const { data: project, isLoading: loadingProject } = useQuery({
    queryKey: ["project_budgets", profile?.id, projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_budgets")
        .select("*")
        .eq("church_id", profile!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data as unknown as ProjectBudget[]) || []).find((entry) => entry.id === projectId) || null;
    },
    enabled: !!profile?.id && !!projectId,
  });

  const { data: allItems = [], isLoading: loadingItems } = useQuery({
    queryKey: ["project_budget_items", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_budget_items")
        .select("*")
        .eq("church_id", profile!.id)
        .order("category");
      if (error) throw error;
      return data as BudgetItem[];
    },
    enabled: !!profile?.id,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "project_budget_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as OrganizationOption[]);
    },
    enabled: !!profile?.id,
  });

  const sortedOrganizations = useMemo(() => sortGeneralFirstByName(organizations), [organizations]);

  const deleteProjectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("project_budgets").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project_budgets"] });
      queryClient.invalidateQueries({ queryKey: ["project_budget_items"] });
      
      navigate("/finance?tab=project_budgets");
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("project_budget_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project_budget_items"] });
      
    },
  });

  const isAllowed = !financeScoped || (!!project?.organization_id && allowedOrgIds.includes(project.organization_id));
  const items = useMemo(() => allItems.filter((item) => item.project_budget_id === projectId), [allItems, projectId]);
  const totalBudgeted = items.reduce((sum, item) => sum + Number(item.budgeted_amount), 0);
  const totalActual = items.reduce((sum, item) => sum + Number(item.actual_amount), 0);
  const pct = project && Number(project.total_budget) > 0 ? Math.min((totalActual / Number(project.total_budget)) * 100, 100) : 0;
  const ministryName = project?.organization_id ? organizations.find((org) => org.id === project.organization_id)?.name : null;

  if (loadingProject || loadingItems) {
    return <div className="stat-card py-12 text-center text-muted-foreground">Loading project...</div>;
  }

  if (!project || !isAllowed) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/finance?tab=project_budgets")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
        </Button>
        <div className="stat-card py-12 text-center text-muted-foreground">Project not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/finance?tab=project_budgets")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
        </Button>
        <div className="shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                if (!requireSubscription()) return;
                setShowProjectForm(true);
              }}>
                <Pencil className="w-4 h-4 mr-2" /> Edit Project
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => {
                if (!requireSubscription()) return;
                deleteProjectMutation.mutate(project.id);
              }}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete Project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="stat-card p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex items-start gap-3">
            <FolderKanban className="mt-1 h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-heading font-bold text-foreground">{project.name}</h1>
                <Badge className={`text-xs ${statusColors[project.status] || ""}`}>{project.status}</Badge>
              </div>
              {ministryName && <p className="mt-1 text-sm text-primary/70">{ministryName}</p>}
              {project.description && <p className="mt-2 text-sm text-muted-foreground">{project.description}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs text-muted-foreground">Budget</p>
              <p className="text-lg font-semibold text-foreground">{formatCurrency(Number(project.total_budget))}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Spent</p>
              <p className={`text-lg font-semibold ${totalActual > Number(project.total_budget) ? "text-destructive" : "text-foreground"}`}>
                {formatCurrency(totalActual)}
              </p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <p className="text-xs text-muted-foreground">Usage</p>
              <Progress value={pct} className="mt-2 h-2" />
              <p className="mt-1 text-xs text-muted-foreground">{pct.toFixed(0)}% of budget</p>
            </div>
          </div>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b border-border px-5 py-3 bg-secondary/10">
            <p className="text-xs font-medium uppercase text-muted-foreground">Line Items</p>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => {
                if (!requireSubscription()) return;
                setEditItem(null);
                setShowItemForm(true);
              }}
            >
              {isPreview && <span className="mr-1"></span>}
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          </div>

          {items.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground">No line items yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-sm">
                <thead>
                  <tr className="border-b border-border/50 bg-secondary/5">
                    <th className="px-5 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Category</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Budgeted</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Actual</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Variance</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const variance = Number(item.budgeted_amount) - Number(item.actual_amount);
                    return (
                      <tr key={item.id} className="border-b border-border/30 transition-colors hover:bg-primary/5">
                        <td className="px-5 py-3 text-foreground">{item.category}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{formatCurrency(Number(item.budgeted_amount))}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{formatCurrency(Number(item.actual_amount))}</td>
                        <td className={`px-4 py-3 text-right font-medium ${variance >= 0 ? "text-success" : "text-destructive"}`}>
                          {formatCurrency(variance)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <button
                              className="rounded p-1 hover:bg-secondary"
                              onClick={() => {
                                if (!requireSubscription()) return;
                                setEditItem(item);
                                setShowItemForm(true);
                              }}
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground" />
                            </button>
                            <button
                              className="rounded p-1 hover:bg-destructive/10"
                              onClick={() => {
                                if (!requireSubscription()) return;
                                deleteItemMutation.mutate(item.id);
                              }}
                            >
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-secondary/20">
                    <td className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total</td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground">{formatCurrency(totalBudgeted)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground">{formatCurrency(totalActual)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${totalBudgeted - totalActual >= 0 ? "text-success" : "text-destructive"}`}>
                      {formatCurrency(totalBudgeted - totalActual)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ProjectForm
        open={showProjectForm}
        onClose={() => setShowProjectForm(false)}
        project={project}
        churchId={profile?.id || ""}
        userId={user?.id || ""}
        organizations={sortedOrganizations}
        scopedOrgIds={financeScoped ? allowedOrgIds : null}
      />

      <ItemForm
        open={showItemForm}
        onClose={() => {
          setShowItemForm(false);
          setEditItem(null);
        }}
        item={editItem}
        projectId={project.id}
        churchId={profile?.id || ""}
      />
    </div>
  );
}
